import { Fragment, useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Pagination,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useNavigate, useParams } from "react-router-dom";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import { useSelector } from "react-redux";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import AlertMessages from "common/Alert/AlertMessages";
import SimpleModal from "common/Form/Form";
import Loader from "layout/Loader";
import { brandingInfo } from "features/server/brandingSlice";
import { selectUserInfo } from "features/user/userSlice";
import { themes } from "theme/themes";
import { SubscriptionPlanProfileService } from "subModule/src/core/services";
import { useGetFeaturesListService } from "subModule/src/services/subscriptionplans/useSubscriptionPlan";
import { GetSubscriptionPlanByIdResponse } from "subModule/src/services/subscriptionplans/interface";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
const FEATURE_TYPE_MAP: Record<number, string> = {
  1: "limit",
  2: "tier",
  3: "boolean",
  4: "Usage",
};
const FEATURES_PAGE_SIZE = 200;
interface FormData {
  [key: string]: any;
}
export default function SubscriptionPlansModal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const isMediumScreen = useMediaQuery("(max-width: 899px)");
  const { enqueueSnackbar } = useSnackbar();
  const loggedInUserData = useSelector(selectUserInfo);
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const billingCycleOptions = [
    { value: 1, label: t("monthly") },
    { value: 3, label: t("weekly") },
    { value: 2, label: t("yearly") },
  ];
  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    trigger,
    formState: { errors, dirtyFields, isDirty },
  } = useForm<FormData>({
    defaultValues: {
      name: "",
      description: "",
      price: "",
      planFlexPriceId: "",
      billingCycle: billingCycleOptions[0],
      planFeature: [],
    },
  });
  const { fields, replace } = useFieldArray({
    control,
    name: "planFeature",
    keyName: "_rhfId",
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(
    params?.id ? false : true,
  );
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    moduleLabelKey: "subscriptionPlans",
    moduleHref: "/subscription-plans",
    entityFallbackLabelKey: "name",
    isEdit: !!params?.id,
  });
  const [featuresPage, setFeaturesPage] = useState(1);
  const [featuresTotalPages, setFeaturesTotalPages] = useState(1);
  const [expandedFeatureId, setExpandedFeatureId] = useState<
    number | string | null
  >(null);
  const { refetch: featuresRefetch, isFetching: isFeaturesFetching } =
    useGetFeaturesListService({
      pageNumber: featuresPage,
      size: FEATURES_PAGE_SIZE,
    });
  useEffect(() => {
    if (params?.id) {
      if (loading) getByID();
    }
  }, [params]);
  useEffect(() => {
    if (params?.id) return;
    featuresRefetch().then((res: any) => {
      if (!res?.data?.pages[0]?.success) {
        enqueueSnackbar(
          <AlertMessages
            text={res?.data?.pages[0]?.message || "Something went wrong"}
          />,
          { variant: "error" },
        );
        if (loading) setLoading(false);
        return;
      }
      const pageData = res?.data?.pages[0]?.data;
      setFeaturesTotalPages(pageData?.totalPages || 1);
      const mapped = (pageData?.content || []).map((feature: any) => ({
        featureId: feature.id,
        name: feature.name,
        description: feature.description,
        type: feature.type,
        enable: false,
        unlimitedAccess: false,
        maxLimit: 1,
        planFeatureItem: (feature.featureItems || []).map((item: any) => ({
          featureItemId: item.id,
          value: item.value,
          enable: false,
          maxLimit: 1,
        })),
      }));
      replace(mapped);
      if (loading) {
        reset({
          name: "",
          description: "",
          price: "",
          planFlexPriceId: "",
          billingCycle: billingCycleOptions[0],
          planFeature: mapped,
        });
        setLoading(false);
      }
    });
  }, [featuresPage]);
  useEffect(() => {
    if (isDirty && params?.id) setIsBtnEnabled(true);
  }, [isDirty, dirtyFields]);
  const getByID = () => {
    setTimeout(() => setLoading(true), 20);
    SubscriptionPlanProfileService.getSubscriptionPlanById(+(params as any)?.id)
      .then((res: GetSubscriptionPlanByIdResponse) => {
        if (res.success) {
          reset({
            ...(res?.data || {}),
            description: res?.data?.description || "",
            planFlexPriceId: (res?.data as any)?.planFlexPriceId || "",
            billingCycle:
              billingCycleOptions.find(
                (o) => o.value === res?.data?.billingCycle,
              ) || billingCycleOptions[0],
            planFeature: (res?.data?.planFeature || []).map((pf: any) => {
              const BYTES_PER_GB = 1024 * 1024 * 1024;
              const isUnlimited = Number(pf.maxLimit) === -1;
              const maxLimit = isUnlimited
                ? -1
                : Number(pf.featureId) === 2201 && pf.maxLimit != null
                  ? Number(pf.maxLimit) / BYTES_PER_GB
                  : pf.maxLimit;
              return {
                ...pf,
                enable: pf.enable ?? false,
                unlimitedAccess: isUnlimited,
                maxLimit,
                planFeatureItem: (pf.planFeatureItem || []).map(
                  (item: any) => ({
                    ...item,
                    enable: item.enable ?? false,
                  }),
                ),
              };
            }),
          });
          captureLabel(res?.data?.name);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
      })
      .finally(() => setLoading(false));
  };
  const handleFeaturesPageChange = (_: any, page: number) => {
    if (page === featuresPage) return;
    setExpandedFeatureId(null);
    setFeaturesPage(page);
  };
  const onSubmit = (data: any) => {
    setIsBtnEnabled(false);
    const isUpdate = !!params?.id;
    const planFeature = (data.planFeature || [])
      .filter((pf: any) =>
        isUpdate ? pf.enable === true || !!pf.id : pf.enable !== false,
      )
      .map((pf: any) => {
        const feature: any = {
          featureId: pf.featureId,
          enable: !!pf.enable,
          planFeatureItem: (pf.planFeatureItem || [])
            .filter((item: any) =>
              isUpdate
                ? item.enable === true || !!item.id
                : item.enable !== false,
            )
            .map((item: any) => {
              const planFeatureItem: any = {
                featureItemId: item.featureItemId,
                enable: !!item.enable,
              };
              if (isUpdate) {
                planFeatureItem.id = item.id;
                planFeatureItem.planFeatureId = item.planFeatureId;
              }
              if (pf.type === 1 || pf.type === 4) {
                planFeatureItem.maxLimit =
                  item.maxLimit === "" || item.maxLimit == null
                    ? 1
                    : Number(item.maxLimit);
              }
              return planFeatureItem;
            }),
        };
        if (isUpdate) {
          feature.id = pf.id;
          feature.planId = pf.planId;
          feature.type = pf.type;
        }
        if (pf.type === 1 || pf.type === 4) {
          let maxLimit =
            pf.maxLimit === "" || pf.maxLimit == null ? 1 : Number(pf.maxLimit);
          // Feature 2201 stores capacity in GB in the UI; API expects bytes (skip for unlimited -1)
          if (Number(pf.featureId) === 2201 && maxLimit !== -1) {
            maxLimit = maxLimit * 1024 * 1024 * 1024;
          }
          feature.maxLimit = maxLimit;
        }
        return feature;
      });
    if (params?.id) {
      SubscriptionPlanProfileService.updateSubscriptionPlan(+params.id, {
        name: data.name?.trim(),
        description: data.description?.trim() || "",
        price: Number(data.price),
        billingCycle: data.billingCycle?.value,
        planFlexPriceId: data.planFlexPriceId?.trim() || "",
        planFeature,
      })
        .then((response: any) => {
          if (response.success) {
            captureLabel(data?.name?.trim());
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("subscriptionPlan") +
                  " " +
                  t("updated") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else {
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
            setIsBtnEnabled(true);
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            {
              variant: "error",
            },
          );
        });
    } else {
      SubscriptionPlanProfileService.createSubscriptionPlan({
        name: data.name?.trim(),
        description: data.description?.trim() || "",
        price: Number(data.price),
        billingCycle: data.billingCycle?.value,
        planFlexPriceId: data.planFlexPriceId?.trim() || "",
        planFeature,
      })
        .then((response: any) => {
          if (response.success) {
            enqueueSnackbar(
              <AlertMessages
                statusCode={response.status}
                text={
                  t("subscriptionPlan") +
                  " " +
                  t("created") +
                  " " +
                  t("successfully")
                }
              />,
              { variant: "success" },
            );
            navigate(-1);
          } else {
            setIsBtnEnabled(true);
            enqueueSnackbar(<AlertMessages text={response?.message} />, {
              variant: "error",
            });
          }
        })
        .catch((err: any) => {
          console.error("===", err);
          setIsBtnEnabled(true);
          enqueueSnackbar(
            <AlertMessages
              text={
                err?.message || err?.data?.message || "Something went wrong"
              }
            />,
            {
              variant: "error",
            },
          );
        });
    }
  };
  const getFeatureTypeLabel = (type?: number) =>
    t(FEATURE_TYPE_MAP[type || 0] || "limit");
  const validateLimitOptional = (value: any) => {
    if (value === "" || value === null || value === undefined) return true;
    const num = Number(value);
    if (num === -1) return true;
    if (isNaN(num) || num <= 0) return t("limitMustBeGreaterThanZero");
    return true;
  };
  const SWITCH_WIDTH = 36;
  const SWITCH_THUMB_SIZE = 14;
  const SWITCH_CHECKED_TRANSLATE_X = SWITCH_WIDTH - SWITCH_THUMB_SIZE - 4;
  const switchStyles = {
    width: SWITCH_WIDTH,
    height: 18,
    p: 0,
    "& .MuiSwitch-switchBase": {
      p: "2px",
      color: "#fff",
      transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
      "&.Mui-checked": {
        transform: `translateX(${SWITCH_CHECKED_TRANSLATE_X}px)`,
        "& + .MuiSwitch-track": {
          bgcolor: themeColors?.secondary,
          opacity: 1,
        },
      },
    },
    "& .MuiSwitch-thumb": {
      width: SWITCH_THUMB_SIZE,
      height: SWITCH_THUMB_SIZE,
      boxShadow: "none",
      background: "#fff",
    },
    "& .MuiSwitch-track": {
      borderRadius: "21px",
      bgcolor: themeColors?.button,
      opacity: 1,
    },
  };
  return (
    <Box>
      <ComponentHeaderTwo
        showBtn={true}
        showSaveBtn={true}
        onClick={() => navigate(-1)}
        onSubmit={handleSubmit(onSubmit)}
        heading={
          params?.id ? t("editSubscriptionPlan") : t("addSubscriptionPlan")
        }
        icon={
          <CardMembershipOutlinedIcon
            sx={{
              fill: "#000",
              width: "30px",
              height: "30px",
              marginTop: "3px",
            }}
          />
        }
        btnText={"back"}
        btnType={"back"}
        isBtnEnabled={IsBtnEnabled}
        showCreateBtn={loggedInUserData?.type === -1}
        breadcrumbItems={breadcrumbItems}
      />
      {loading ? (
        <Loader />
      ) : (
        <SimpleModal
          visable={true}
          title={params?.id ? "editSubscriptionPlan" : "addSubscriptionPlan"}
          isButtonVisable={true}
          isVisableBtn={IsBtnEnabled}
          btnclosetext="Back"
          buttonText={params?.id ? "update" : "submit"}
          attBtnNotshow={false}
          notVisableBackbtn={true}
          showPortList={handleSubmit(onSubmit)}
          formSubmit={handleSubmit(onSubmit)}
          padding={false}
          content={
            <Grid
              container
              component="form"
              id="form"
              spacing={2.5}
              className="mt-0"
              onSubmit={handleSubmit(onSubmit)}
            >
              <Grid
                item
                xs={12}
                lg={4}
                className={`${isMediumScreen ? "" : "sticky"} top-[43px] z-20 self-start`}
              >
                <Paper
                  elevation={0}
                  className="rounded-xl p-5"
                  sx={{
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    border: "1px solid #ECECEC",
                  }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        {...register("name", {
                          validate: {
                            required: (value) =>
                              (value?.trim() !== "" && value != null) ||
                              t("This field is required."),
                          },
                        })}
                        label={t("name") + " *"}
                        placeholder={t("phSubscriptionPlanName")}
                        title={t("name") + " *"}
                        fullWidth
                        error={!!errors.name}
                        helperText={errors.name?.message as string}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            background: "#fff",
                          },
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        {...register("planFlexPriceId", {
                          validate: {
                            required: (value) =>
                              String(value || "").trim() !== "" ||
                              t("This field is required."),
                          },
                        })}
                        label={t("planCode") + " *"}
                        placeholder={t("phSubscriptionPlanCode")}
                        title={t("planCode")}
                        fullWidth
                        error={!!errors.planFlexPriceId}
                        helperText={errors.planFlexPriceId?.message as string}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            background: "#fff",
                          },
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        {...register("description")}
                        label={t("description")}
                        placeholder={t("phSubscriptionPlanDescription")}
                        title={t("description")}
                        fullWidth
                        multiline
                        minRows={3}
                        maxRows={6}
                        error={!!errors.description}
                        helperText={errors.description?.message as string}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            background: "#fff",
                          },
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography className="font-Saira text-[12px] font-semibold text-[#4B5563] text-left mb-1.5">
                        {t("billingCycle") + " *"}
                      </Typography>
                      <Controller
                        name="billingCycle"
                        control={control}
                        rules={{ required: t("This field is required.") }}
                        render={({ field }) => (
                          <Box>
                            <RadioGroup
                              row
                              aria-labelledby="billing-cycle-radio-group"
                              {...field}
                              value={field.value?.value || ""}
                              onChange={(e: any) => {
                                const selected = billingCycleOptions.find(
                                  (o) => o.value === Number(e.target.value),
                                );
                                field.onChange(selected || null);
                                trigger("billingCycle");
                              }}
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: isMediumScreen ? "balance" : "nowrap",
                                gap: "8px",
                                width: "100%",
                                "& .MuiRadio-root": { display: "none" },
                                "& .MuiFormControlLabel-root": {
                                  flex: isMediumScreen ? "none" : 1,
                                  margin: "0 !important",
                                  justifyContent: "center",
                                  background: "#fff",
                                  padding: "6px 15px",
                                  borderRadius: "8px",
                                  border: "1px solid",
                                  borderColor: themeColors?.primary,
                                },
                                "& .MuiFormControlLabel-root:has(.Mui-checked)":
                                  {
                                    border: "1px solid",
                                    borderColor: themeColors?.primary,
                                    background: themeColors?.primary,
                                  },
                                "& .MuiFormControlLabel-root:hover": {
                                  outline: "1px solid",
                                  outlineColor: themeColors?.primary,
                                },
                                "& .MuiFormControlLabel-root:has(.Mui-checked) .MuiTypography-root":
                                  {
                                    color: "#fff !important",
                                    fontWeight: 700,
                                  },
                                "& .MuiFormControlLabel-label": {
                                  fontFamily: "Saira, sans-serif",
                                  fontWeight: 600,
                                  fontSize: "13px !important",
                                  color: themeColors?.primary + " !important",
                                  width: "100%",
                                  textAlign: "center",
                                },
                              }}
                            >
                              {billingCycleOptions.map((option) => (
                                <FormControlLabel
                                  key={option.value}
                                  value={option.value}
                                  control={<Radio />}
                                  label={option.label}
                                />
                              ))}
                            </RadioGroup>
                          </Box>
                        )}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        {...register("price", {
                          required: t("This field is required."),
                          validate: (value) => {
                            const num = Number(value);
                            if (isNaN(num) || num <= 0)
                              return t("limitMustBeGreaterThanZero");
                            return true;
                          },
                        })}
                        type="number"
                        label={
                          t("basePrice") +
                          (loggedInUserData?.business?.currency
                            ? " (" +
                              (loggedInUserData?.business?.currency?.toUpperCase() ||
                                "") +
                              ")"
                            : "") +
                          " *"
                        }
                        placeholder={t("phSubscriptionPlanPrice")}
                        title={
                          t("basePrice") +
                          (loggedInUserData?.business?.currency
                            ? " (" +
                              (loggedInUserData?.business?.currency?.toUpperCase() ||
                                "") +
                              ")"
                            : "") +
                          " *"
                        }
                        fullWidth
                        error={!!errors.price}
                        helperText={errors.price?.message as string}
                        inputProps={{ min: 0, step: "0.01" }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            background: "#fff",
                          },
                          "& input[type=number]": {
                            MozAppearance: "textfield",
                          },
                          "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                            {
                              WebkitAppearance: "none",
                              margin: 0,
                            },
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item xs={12} lg={8}>
                <Paper
                  elevation={0}
                  className="relative"
                  sx={{
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    border: "1px solid #ECECEC",
                    position: "relative",
                    borderRadius: "8px",
                    overflow: "visible",
                  }}
                >
                  {(!params?.id && isFeaturesFetching) ||
                  (params?.id && loading) ? (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(0, 0, 0, 0.1)",
                        borderRadius: "8px",
                      }}
                    >
                      <CircularProgress sx={{ color: themeColors?.primary }} />
                    </Box>
                  ) : null}
                  <TableContainer
                    sx={{ borderRadius: "8px", overflow: "visible" }}
                  >
                    <Table
                      sx={{
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        "& .MuiTableCell-root": { padding: "8px" },
                        "& thead .MuiTableCell-head:first-of-type": {
                          borderTopLeftRadius: "8px",
                        },
                        "& thead .MuiTableCell-head:last-of-type": {
                          borderTopRightRadius: "8px",
                        },
                        ...(!params?.id && featuresTotalPages > 1
                          ? {}
                          : {
                              "& tbody tr:last-of-type .MuiTableCell-root:first-of-type":
                                {
                                  borderBottomLeftRadius: "8px",
                                },
                              "& tbody tr:last-of-type .MuiTableCell-root:last-of-type":
                                {
                                  borderBottomRightRadius: "8px",
                                },
                            }),
                      }}
                    >
                      <TableHead
                        className={`${isMediumScreen ? "" : "sticky"} top-[42px] z-[20]`}
                        sx={{
                          background: themeColors?.appBg || "#F3F0F7",
                          "& .MuiTableCell-head": {
                            background: themeColors?.appBg || "#F3F0F7",
                          },
                        }}
                      >
                        <TableRow>
                          <TableCell className="font-Saira font-bold text-[12px] text-[#374151]">
                            {t("featureName")}
                          </TableCell>
                          <TableCell className="font-Saira font-bold text-[12px] text-[#374151]">
                            {t("status")}
                          </TableCell>
                          <TableCell className="font-Saira font-bold text-[12px] text-[#374151]">
                            {t("type")}
                          </TableCell>
                          <TableCell className="font-Saira font-bold text-[12px] text-[#374151]">
                            {t("capacityLimit")}
                          </TableCell>
                          <TableCell className="font-Saira font-bold text-[12px] text-[#374151]">
                            {t("unlimitedAccess")}
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fields.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} align="center">
                              <Typography className="font-Saira text-[13px] text-[#6B7280] py-6">
                                {t("noFeaturesFound")}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          fields.map((field: any, index: number) => {
                            const featureType = field.type;
                            const featureItems = field.planFeatureItem || [];
                            const isExpanded =
                              expandedFeatureId === field._rhfId;
                            const rowBg =
                              index % 2 !== 0 ? themeColors?.appBg : "#fff";
                            const isFeatureEnabled = !!watch(
                              `planFeature.${index}.enable`,
                            );
                            const isUnlimitedAccess = !!watch(
                              `planFeature.${index}.unlimitedAccess`,
                            );
                            const showUnlimitedSwitch =
                              (featureType === 1 || featureType === 4) &&
                              isFeatureEnabled;
                            const showMaxLimit =
                              (featureType === 1 || featureType === 4) &&
                              isFeatureEnabled &&
                              !isUnlimitedAccess;
                            return (
                              <Fragment key={field._rhfId}>
                                <TableRow
                                  sx={{
                                    verticalAlign: "middle",
                                    backgroundColor: rowBg,
                                    "& .MuiTableCell-root": {
                                      backgroundColor: rowBg,
                                    },
                                  }}
                                >
                                  <TableCell>
                                    <Typography className="font-Saira font-bold text-[13px] text-[#111827] text-left">
                                      {t(field.name)}
                                    </Typography>
                                    <Typography className="font-Saira text-[11px] text-[#6B7280] text-left mt-0.5">
                                      {t(field.description)}
                                    </Typography>
                                    <input
                                      type="hidden"
                                      {...register(
                                        `planFeature.${index}.featureId` as const,
                                      )}
                                    />
                                    <input
                                      type="hidden"
                                      {...register(
                                        `planFeature.${index}.type` as const,
                                      )}
                                    />
                                    <input
                                      type="hidden"
                                      {...register(
                                        `planFeature.${index}.name` as const,
                                      )}
                                    />
                                    <input
                                      type="hidden"
                                      {...register(
                                        `planFeature.${index}.description` as const,
                                      )}
                                    />
                                    <input
                                      type="hidden"
                                      {...register(
                                        `planFeature.${index}.id` as const,
                                      )}
                                    />
                                    <input
                                      type="hidden"
                                      {...register(
                                        `planFeature.${index}.planId` as const,
                                      )}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Switch
                                      checked={isFeatureEnabled}
                                      onChange={(e) => {
                                        const checked = e.target.checked;
                                        setValue(
                                          `planFeature.${index}.enable`,
                                          checked,
                                          {
                                            shouldDirty: true,
                                          },
                                        );
                                        if (
                                          featureType === 2 &&
                                          !checked &&
                                          isExpanded
                                        ) {
                                          setExpandedFeatureId(null);
                                          featureItems.forEach(
                                            (_: any, itemIndex: number) => {
                                              setValue(
                                                `planFeature.${index}.planFeatureItem.${itemIndex}.enable`,
                                                false,
                                                { shouldDirty: true },
                                              );
                                            },
                                          );
                                        }
                                      }}
                                      sx={switchStyles}
                                    />
                                    <input
                                      type="hidden"
                                      {...register(
                                        `planFeature.${index}.enable` as const,
                                      )}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Chip
                                      label={getFeatureTypeLabel(featureType)}
                                      size="small"
                                      sx={{
                                        background: "#E5E7EB",
                                        color: "#4B5563",
                                        fontFamily: "Saira, sans-serif",
                                        fontWeight: 600,
                                        fontSize: '11px',
                                        borderRadius: "999px",
                                        height: "24px",
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    {showMaxLimit ? (
                                      <Controller
                                        name={`planFeature.${index}.maxLimit`}
                                        control={control}
                                        rules={{
                                          validate: validateLimitOptional,
                                        }}
                                        render={({ field }) => (
                                          <TextField
                                            {...field}
                                            type="number"
                                            size="small"
                                            placeholder={t("phSubscriptionMaxLimit")}
                                            error={
                                              !!(errors as any).planFeature?.[
                                                index
                                              ]?.maxLimit
                                            }
                                            helperText={
                                              (errors as any).planFeature?.[
                                                index
                                              ]?.maxLimit?.message as string
                                            }
                                            inputProps={{ min: 1, step: 1 }}
                                            onChange={(e) => {
                                              let value: string | number =
                                                e.target.value;
                                              value = Math.max(
                                                0,
                                                Math.floor(Number(value)),
                                              );
                                              if (
                                                Number(value) < 0 ||
                                                !Number(value)
                                              )
                                                value = "";
                                              if (!IsBtnEnabled)
                                                setIsBtnEnabled(true);
                                              field.onChange(value);
                                            }}
                                            onFocus={(e) => {
                                              if (e.target.value === "0") {
                                                e.target.value = "";
                                                field.onChange("");
                                              }
                                            }}
                                            onBlur={(e) => {
                                              if (
                                                e.target.value === "" ||
                                                Number(e.target.value) < 1
                                              ) {
                                                e.target.value = "1";
                                                field.onChange("1");
                                              }
                                              field.onBlur();
                                            }}
                                            sx={{
                                              maxWidth: 140,
                                              "& .MuiOutlinedInput-root": {
                                                borderRadius: "8px",
                                                background: "#fff",
                                              },
                                              "& input[type=number]": {
                                                MozAppearance: "textfield",
                                              },
                                              "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                                                {
                                                  WebkitAppearance: "none",
                                                  margin: 0,
                                                },
                                            }}
                                            InputLabelProps={{ shrink: true }}
                                          />
                                        )}
                                      />
                                    ) : null}
                                  </TableCell>
                                  <TableCell>
                                    {showUnlimitedSwitch ? (
                                      <Switch
                                        checked={isUnlimitedAccess}
                                        onChange={(e) => {
                                          const checked = e.target.checked;
                                          setValue(
                                            `planFeature.${index}.unlimitedAccess`,
                                            checked,
                                            {
                                              shouldDirty: true,
                                            },
                                          );
                                          setValue(
                                            `planFeature.${index}.maxLimit`,
                                            checked ? -1 : 1,
                                            { shouldDirty: true },
                                          );
                                          if (!IsBtnEnabled)
                                            setIsBtnEnabled(true);
                                        }}
                                        sx={switchStyles}
                                      />
                                    ) : null}
                                  </TableCell>
                                  <TableCell>
                                    {featureItems?.length ? (
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          setExpandedFeatureId(
                                            isExpanded ? null : field._rhfId,
                                          )
                                        }
                                        sx={{
                                          transform: isExpanded
                                            ? "rotate(180deg)"
                                            : "rotate(0deg)",
                                          transition: "transform 0.2s",
                                        }}
                                        disabled={!isFeatureEnabled}
                                      >
                                        <ExpandMoreIcon
                                          sx={{
                                            color: !isFeatureEnabled
                                              ? "#9CA3AF"
                                              : "#000",
                                          }}
                                        />
                                      </IconButton>
                                    ) : null}
                                  </TableCell>
                                </TableRow>
                                {featureItems?.length && isExpanded ? (
                                  <TableRow>
                                    <TableCell
                                      colSpan={6}
                                      sx={{
                                        py: 1,
                                        pl: 6,
                                        background: "#FAFAFA",
                                      }}
                                    >
                                      <Box className="flex flex-wrap flex-col gap-1.5">
                                        {featureItems.map(
                                          (item: any, itemIndex: number) => (
                                            <Box
                                              key={`${field._rhfId}-item-${itemIndex}`}
                                              className="flex items-center gap-2"
                                            >
                                              <Switch
                                                checked={
                                                  !!watch(
                                                    `planFeature.${index}.planFeatureItem.${itemIndex}.enable`,
                                                  )
                                                }
                                                onChange={(e) =>
                                                  setValue(
                                                    `planFeature.${index}.planFeatureItem.${itemIndex}.enable`,
                                                    e.target.checked,
                                                    { shouldDirty: true },
                                                  )
                                                }
                                                sx={switchStyles}
                                              />
                                              <Chip
                                                label={t(item.value) || "—"}
                                                size="small"
                                                sx={{
                                                  background:
                                                    themeColors?.appBg ||
                                                    "#F3F4F6",
                                                  color: "#374151",
                                                  fontFamily:
                                                    "Saira, sans-serif",
                                                  fontWeight: 500,
                                                  fontSize: '11px',
                                                  borderRadius: "8px",
                                                  height: "28px",
                                                }}
                                              />
                                              <input
                                                type="hidden"
                                                {...register(
                                                  `planFeature.${index}.planFeatureItem.${itemIndex}.featureItemId` as const,
                                                )}
                                              />
                                              <input
                                                type="hidden"
                                                {...register(
                                                  `planFeature.${index}.planFeatureItem.${itemIndex}.id` as const,
                                                )}
                                              />
                                              <input
                                                type="hidden"
                                                {...register(
                                                  `planFeature.${index}.planFeatureItem.${itemIndex}.planFeatureId` as const,
                                                )}
                                              />
                                              <input
                                                type="hidden"
                                                {...register(
                                                  `planFeature.${index}.planFeatureItem.${itemIndex}.value` as const,
                                                )}
                                              />
                                              <input
                                                type="hidden"
                                                {...register(
                                                  `planFeature.${index}.planFeatureItem.${itemIndex}.enable` as const,
                                                )}
                                              />
                                              <input
                                                type="hidden"
                                                {...register(
                                                  `planFeature.${index}.planFeatureItem.${itemIndex}.maxLimit` as const,
                                                )}
                                              />
                                            </Box>
                                          ),
                                        )}
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                ) : null}
                              </Fragment>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {!params?.id && featuresTotalPages > 1 && (
                    <Box
                      className="flex justify-center py-3"
                      sx={{
                        borderBottomLeftRadius: "8px",
                        borderBottomRightRadius: "8px",
                      }}
                    >
                      <Pagination
                        count={featuresTotalPages}
                        page={featuresPage}
                        onChange={handleFeaturesPageChange}
                        shape="rounded"
                        sx={{
                          "& .MuiPaginationItem-root": {
                            fontFamily: "Saira, sans-serif",
                            fontWeight: 600,
                            color: "#4A4A4A",
                          },
                          "& .MuiPaginationItem-root.Mui-selected": {
                            bgcolor: themeColors?.primary,
                            color: "#fff",
                            "&:hover": { bgcolor: themeColors?.primary },
                          },
                        }}
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>
          }
        />
      )}
    </Box>
  );
}
